(() => {
  'use strict';

  const t = (key, fallback, variables = {}) => window.nawahTranslate?.(key, fallback, variables) || fallback || key;

  const VERSION = '37.1.0';
  const SOURCE = 'nawah-gift-engine-v37';
  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_URL = `${ROOT}cart.js`;
  const CART_UPDATE_URL = `${ROOT}cart/update.js`;
  const CART_ADD_URL = `${ROOT}cart/add.js`;
  const CHECKOUT_URL = `${ROOT}checkout`;
  const CONFIG_NODE = document.getElementById('NawahGiftConfig');

  const OFFER_RULE_SECTION = 'nawah-offer-rule-data';
  const GIFT_VARIANT_SECTION = 'nawah-offer-gift-variant-data';
  const RULE_SCHEMA = 'nawah.offer-rule.v1';
  const MAX_FIXED_GIFTS_PER_CART = 4;
  const offerRuleCache = new Map();
  const giftVariantMetaCache = new Map();

  function numericVariantId(value) {
    const match = String(value || '').match(/(\d+)$/);
    return match ? Number(match[1]) : NaN;
  }

  function normalizedRule(raw) {
    if (!raw || typeof raw !== 'object' || raw.schema !== RULE_SCHEMA || raw.enabled === false || raw.mode === 'none') return null;
    if (raw.quantityMode !== 'per_trigger_unit') return null;
    if (raw.mode === 'fixed' && Array.isArray(raw.gifts)) return raw;
    if (raw.mode === 'customer_choice' && Array.isArray(raw.choice?.variantIds)) return raw;
    return null;
  }

  async function fetchSectionJson(url, selector) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok) throw createRequestError(response.status, `Unable to read NAWAH offer data (${response.status}).`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    if (!node) throw new Error('NAWAH offer data response did not contain the expected payload.');
    return JSON.parse(node.textContent || 'null');
  }

  async function offerRuleForHandle(handle) {
    const key = String(handle || '').trim();
    if (!key) return null;
    if (offerRuleCache.has(key)) return offerRuleCache.get(key);
    const promise = fetchSectionJson(`${ROOT}products/${encodeURIComponent(key)}?section_id=${OFFER_RULE_SECTION}`, '[data-nawah-offer-rule-data]')
      .then((payload) => normalizedRule(payload?.rule))
      .catch((error) => {
        console.warn(`NAWAH: offer rule could not be read for ${key}.`, error);
        return null;
      });
    offerRuleCache.set(key, promise);
    return promise;
  }

  let config = null;
  try {
    config = JSON.parse(CONFIG_NODE?.textContent || '{}');
  } catch (error) {
    console.warn('NAWAH gift configuration could not be read.', error);
  }

  function configuredGiftVariantMeta(id) {
    const standardGift = Object.values(config?.gifts || {}).find((gift) => numericVariantId(gift?.variantId) === id);
    if (standardGift) {
      return {
        variantId: id,
        productId: null,
        productHandle: standardGift.handle || '',
        productTitle: standardGift.label || 'Complimentary gift',
        variantTitle: '',
        available: standardGift.available !== false,
        image: standardGift.image || null,
      };
    }

    const bodySplashVariant = (config?.bodySplash?.variants || []).find((variant) => numericVariantId(variant?.variantId) === id);
    if (bodySplashVariant) {
      return {
        variantId: id,
        productId: config?.bodySplash?.productId || null,
        productHandle: config?.bodySplash?.handle || '',
        productTitle: config?.bodySplash?.label || 'UNO Body Splash',
        variantTitle: bodySplashVariant.name || '',
        available: bodySplashVariant.available !== false,
        image: bodySplashVariant.image || null,
      };
    }

    return null;
  }

  async function giftVariantMeta(variantRef) {
    const id = numericVariantId(variantRef);
    if (!Number.isFinite(id)) return null;
    if (giftVariantMetaCache.has(id)) return giftVariantMetaCache.get(id);

    const configured = configuredGiftVariantMeta(id);
    if (configured) {
      const promise = Promise.resolve(configured);
      giftVariantMetaCache.set(id, promise);
      return promise;
    }

    const promise = fetchSectionJson(`${ROOT}variants/${id}?section_id=${GIFT_VARIANT_SECTION}`, '[data-nawah-gift-variant-data]')
      .then((payload) => ({
        variantId: Number(payload?.variantId || id),
        productId: payload?.productId || null,
        productHandle: payload?.productHandle || '',
        productTitle: payload?.productTitle || 'Complimentary gift',
        variantTitle: payload?.variantTitle || '',
        available: payload?.available !== false,
        image: payload?.image || null,
      }))
      .catch((error) => {
        console.warn(`NAWAH: gift variant data could not be read for ${id}.`, error);
        return { variantId: id, productTitle: 'Complimentary gift', variantTitle: '', available: false, image: null };
      });
    giftVariantMetaCache.set(id, promise);
    return promise;
  }

  const engineReady = true;

  let debounceTimer = null;
  let activeFlush = null;
  let requestedRevision = 0;
  let completedRevision = 0;
  let nativeMutationDepth = 0;
  let latestCartHint = null;
  let lastCart = null;
  let cartKnownClean = false;
  let checkoutInProgress = false;
  let syncing = false;
  let lastMutationAt = 0;

  const MIN_MUTATION_INTERVAL = 260;
  const DEFAULT_DEBOUNCE = 90;

  function propertyValue(item, key) {
    return item.properties?.[key] ?? item.properties?.[key.replace(/^_/, '')] ?? null;
  }

  function isAutoGift(item) {
    return String(propertyValue(item, '_nawah_auto_gift')).toLowerCase() === 'true';
  }

  function isGiftVariant(item) {
    return isAutoGift(item);
  }

  function isCustomBundleLine(item) {
    const handle = item.handle || item.product_handle || '';
    return handle.startsWith('nawah-cyob-')
      || Boolean(item.parent_relationship || item.parent_id)
      || String(propertyValue(item, '_nawah_cyob')).toLowerCase() === 'true'
      || String(propertyValue(item, '_nawah_cyob_component')).toLowerCase() === 'true';
  }

  function parseChoiceIds(item, rule) {
    const property = String(rule?.choice?.selectionProperty || '_nawah_offer_choices');
    const raw = propertyValue(item, property);
    if (!raw) return [];
    const allowed = new Set((rule?.choice?.variantIds || []).map(numericVariantId).filter(Number.isFinite));
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.map(Number).filter((value) => Number.isFinite(value) && allowed.has(value));
    } catch (error) {
      console.warn('NAWAH: invalid customer-choice gift data on cart line.', error);
      return [];
    }
  }

  async function calculateDesired(cart) {
    const targets = {};
    const definitions = new Map();
    const parentUpdates = {};
    const notices = [];
    let fixedGiftSlotsRemaining = MAX_FIXED_GIFTS_PER_CART;

    for (const item of cart?.items || []) {
      if (isAutoGift(item) || isCustomBundleLine(item)) continue;
      const handle = item.handle || item.product_handle || '';
      const quantity = Math.max(0, Number(item.quantity || 0));
      if (!handle || quantity <= 0) continue;
      const rule = await offerRuleForHandle(handle);
      if (!rule) continue;

      if (rule.mode === 'fixed') {
        const validGifts = (rule.gifts || [])
          .map((gift) => ({
            variantId: numericVariantId(gift.variantId),
            giftQty: Math.max(0, Number(gift.quantity || 0)),
          }))
          .filter((gift) => Number.isFinite(gift.variantId) && gift.giftQty > 0);

        const giftsPerUnit = validGifts.reduce((sum, gift) => sum + gift.giftQty, 0);
        if (giftsPerUnit <= 0 || fixedGiftSlotsRemaining < giftsPerUnit) continue;

        const grantedUnits = Math.min(quantity, Math.floor(fixedGiftSlotsRemaining / giftsPerUnit));
        if (grantedUnits <= 0) continue;

        for (const gift of validGifts) {
          targets[String(gift.variantId)] = Number(targets[String(gift.variantId)] || 0) + grantedUnits * gift.giftQty;
          if (!definitions.has(gift.variantId)) definitions.set(gift.variantId, await giftVariantMeta(gift.variantId));
        }
        fixedGiftSlotsRemaining -= grantedUnits * giftsPerUnit;
        continue;
      }

      if (rule.mode === 'customer_choice') {
        const requiredPerUnit = Math.max(1, Number(rule.choice?.quantity || 1));
        const choices = parseChoiceIds(item, rule);
        const needed = quantity * requiredPerUnit;
        const usable = choices.slice(0, needed);
        const completeUnits = Math.floor(usable.length / requiredPerUnit);
        if (completeUnits < quantity) {
          parentUpdates[item.key] = completeUnits;
          notices.push(
            completeUnits > 0
              ? t('dry_oil_quantity_reduced', '{{ product }} quantity was reduced to match the complimentary gift choices selected on its product page.', { product: item.product_title || 'Product' })
              : t('dry_oil_removed_missing_scent', '{{ product }} was removed because every unit requires a complimentary gift selection.', { product: item.product_title || 'Product' })
          );
        }
        for (const variantId of usable) {
          targets[String(variantId)] = Number(targets[String(variantId)] || 0) + 1;
          if (!definitions.has(Number(variantId))) definitions.set(Number(variantId), await giftVariantMeta(variantId));
        }
      }
    }

    return { targets, giftDefinitions: [...definitions.values()].filter(Boolean), parentUpdates, notices };
  }

  function createRequestError(status, message) {
    const error = new Error(String(message));
    error.status = Number(status || 0);
    return error;
  }

  async function fetchCart() {
    const response = await fetch(CART_URL, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw createRequestError(response.status, t('unable_to_read_cart', 'Unable to read cart ({{ status }}).', { status: response.status }));
    return response.json();
  }

  async function postJsonOnce(url, payload) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status || data.errors) {
      const message = data.description || data.message || data.errors || t('cart_update_failed', 'Cart update failed ({{ status }}).', { status: response.status });
      throw createRequestError(response.status, message);
    }
    return data;
  }

  async function postJson(url, payload) {
    try {
      return await postJsonOnce(url, payload);
    } catch (error) {
      if (Number(error?.status) !== 429) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, 700));
      return postJsonOnce(url, payload);
    }
  }

  function giftLines(cart, gift) {
    return (cart?.items || []).filter((item) => Number(item.variant_id) === Number(gift.variantId) && isAutoGift(item));
  }

  function giftProperties(gift) {
    const displayName = gift.variantTitle && gift.variantTitle !== 'Default Title'
      ? `${gift.productTitle} — ${gift.variantTitle}`
      : gift.productTitle || 'Complimentary gift';
    return {
      _nawah_auto_gift: 'true',
      _nawah_gift_name: displayName,
      _nawah_gift_engine: VERSION,
      _nawah_gift_kind: 'offer-rule',
    };
  }

  function buildPlan(cart, desired) {
    const updates = { ...(desired.parentUpdates || {}) };
    const additions = [];
    const definitions = new Map((desired.giftDefinitions || []).map((gift) => [Number(gift.variantId), gift]));

    for (const item of cart?.items || []) {
      if (!isAutoGift(item)) continue;
      const variantId = Number(item.variant_id);
      if (!definitions.has(variantId)) {
        definitions.set(variantId, {
          variantId,
          productTitle: propertyValue(item, '_nawah_gift_name') || item.product_title || 'Complimentary gift',
          variantTitle: '',
          available: true,
        });
      }
    }

    for (const gift of definitions.values()) {
      const targetQuantity = Math.max(0, Number(desired.targets?.[String(gift.variantId)] || 0));
      const lines = giftLines(cart, gift);
      const managedLine = lines[0] || null;

      if (targetQuantity === 0 || gift.available === false) {
        for (const line of lines) updates[line.key] = 0;
        continue;
      }

      if (managedLine) {
        if (Number(managedLine.quantity) !== targetQuantity) updates[managedLine.key] = targetQuantity;
        for (const line of lines.slice(1)) updates[line.key] = 0;
        continue;
      }

      additions.push({ id: Number(gift.variantId), quantity: targetQuantity, properties: giftProperties(gift) });
    }

    return { updates, additions, notices: desired.notices || [] };
  }

  function planHasChanges(plan) {
    return Object.keys(plan.updates).length > 0 || plan.additions.length > 0;
  }

  function collectSectionTargets() {
    const targets = [];
    const mainItems = document.getElementById('main-cart-items');
    const mainFooter = document.getElementById('main-cart-footer');

    if (mainItems?.dataset?.id) {
      targets.push({ requestId: mainItems.dataset.id, targetId: 'main-cart-items', selector: '.js-contents' });
    }
    if (mainFooter?.dataset?.id) {
      targets.push({ requestId: mainFooter.dataset.id, targetId: 'main-cart-footer', selector: '.js-contents' });
    }
    if (document.querySelector('cart-drawer')) {
      targets.push({ requestId: 'cart-drawer', targetSelector: 'cart-drawer', sourceSelector: 'cart-drawer' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      targets.push({ requestId: 'cart-icon-bubble', targetId: 'cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.getElementById('cart-live-region-text')) {
      targets.push({ requestId: 'cart-live-region-text', targetId: 'cart-live-region-text', selector: '.shopify-section' });
    }

    const seen = new Set();
    return targets.filter((target) => {
      if (seen.has(target.requestId)) return false;
      seen.add(target.requestId);
      return true;
    });
  }

  function sectionPayload(targets) {
    if (!targets.length) return {};
    return {
      sections: targets.map((target) => target.requestId),
      sections_url: window.location.pathname || ROOT,
    };
  }

  function applyReturnedSections(sections, targets) {
    if (!sections || typeof sections !== 'object') return;
    const parser = new DOMParser();

    for (const targetInfo of targets) {
      const html = sections[targetInfo.requestId];
      if (!html) continue;
      const doc = parser.parseFromString(html, 'text/html');

      if (targetInfo.targetSelector) {
        const target = document.querySelector(targetInfo.targetSelector);
        const source = doc.querySelector(targetInfo.sourceSelector || targetInfo.targetSelector);
        if (target && source) target.innerHTML = source.innerHTML;
        continue;
      }

      const targetRoot = document.getElementById(targetInfo.targetId);
      if (!targetRoot) continue;
      const sourceRoot = doc.getElementById(`shopify-section-${targetInfo.requestId}`) || doc;
      const target = targetInfo.selector ? targetRoot.querySelector(targetInfo.selector) || targetRoot : targetRoot;
      const source = targetInfo.selector ? sourceRoot.querySelector(targetInfo.selector) || doc.querySelector(targetInfo.selector) : sourceRoot;
      if (target && source) target.innerHTML = source.innerHTML;
    }
  }

  function updateCartCounters(cart) {
    const count = Number(cart?.item_count || 0);
    document.querySelectorAll('[data-nawah-cart-count]').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count === 0;
    });
  }

  function clearCartErrors() {
    for (const id of ['cart-errors', 'CartDrawer-CartErrors']) {
      const element = document.getElementById(id);
      if (!element) continue;
      element.textContent = '';
      delete element.dataset.nawahGiftError;
    }
  }

  function showCheckoutError(message) {
    const text = message || t('gifts_still_updating', 'Complimentary gifts are still updating. Please try checkout again.');
    let shown = false;
    for (const id of ['cart-errors', 'CartDrawer-CartErrors']) {
      const element = document.getElementById(id);
      if (!element) continue;
      element.dataset.nawahGiftError = 'true';
      element.textContent = text;
      shown = true;
    }
    if (!shown) console.error(text);
  }

  function ensureSyncStatus() {
    let status = document.getElementById('NawahGiftSyncStatus');
    if (status) return status;

    const host = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (!host) return null;

    status = document.createElement('div');
    status.id = 'NawahGiftSyncStatus';
    status.className = 'nawah-gift-sync-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.innerHTML = `<span class="nawah-gift-sync-status__spinner" aria-hidden="true"></span><span>${t('updating_complimentary_gifts', 'Updating complimentary gifts…')}</span>`;
    host.prepend(status);
    return status;
  }

  function setSyncing(value) {
    syncing = Boolean(value);
    document.documentElement.dataset.nawahGiftsSyncing = syncing ? 'true' : 'false';
    const status = syncing ? ensureSyncStatus() : document.getElementById('NawahGiftSyncStatus');
    if (!syncing && status) status.remove();
  }

  async function waitForMutationSlot() {
    const elapsed = Date.now() - lastMutationAt;
    if (elapsed < MIN_MUTATION_INTERVAL) {
      await new Promise((resolve) => window.setTimeout(resolve, MIN_MUTATION_INTERVAL - elapsed));
    }
  }

  async function waitForNativeIdle() {
    while (nativeMutationDepth > 0) {
      await new Promise((resolve) => window.setTimeout(resolve, 35));
    }
  }

  async function mutatePlan(plan) {
    await waitForNativeIdle();
    await waitForMutationSlot();
    lastMutationAt = Date.now();

    const targets = collectSectionTargets();
    const renderPayload = sectionPayload(targets);
    let responseData = null;

    if (Object.keys(plan.updates).length > 0) {
      responseData = await postJson(CART_UPDATE_URL, { updates: plan.updates, ...renderPayload });
    }
    if (plan.additions.length > 0) {
      responseData = await postJson(CART_ADD_URL, { items: plan.additions, ...renderPayload });
    }

    if (responseData?.sections) applyReturnedSections(responseData.sections, targets);

    const cart = plan.additions.length > 0 ? await fetchCart() : responseData;
    updateCartCounters(cart);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
    }
    document.dispatchEvent(new CustomEvent('nawah:gifts-synced', { detail: { cart } }));
    return cart;
  }

  async function reconcileOnce(cartHint = null) {
    if (!engineReady) return null;
    await waitForNativeIdle();
    setSyncing(true);

    let cart = cartHint?.items ? cartHint : await fetchCart();

    for (let pass = 0; pass < 2; pass += 1) {
      const plan = buildPlan(cart, await calculateDesired(cart));
      if (!planHasChanges(plan)) break;
      cart = await mutatePlan(plan);
    }

    const finalPlan = buildPlan(cart, await calculateDesired(cart));
    if (planHasChanges(finalPlan)) {
      cart = await mutatePlan(finalPlan);
    }

    lastCart = cart;
    cartKnownClean = !planHasChanges(buildPlan(cart, await calculateDesired(cart)));
    clearCartErrors();
    setSyncing(false);
    return cart;
  }

  async function runFlushLoop() {
    if (!engineReady) return null;

    try {
      while (completedRevision < requestedRevision) {
        const revision = requestedRevision;
        const hint = latestCartHint;
        latestCartHint = null;
        const cart = await reconcileOnce(hint);
        completedRevision = revision;
        lastCart = cart || lastCart;
      }
      return lastCart;
    } catch (error) {
      completedRevision = requestedRevision;
      cartKnownClean = false;
      setSyncing(false);
      console.error('NAWAH gift reconciliation failed:', error);
      document.dispatchEvent(new CustomEvent('nawah:gifts-error', { detail: { message: error.message } }));
      return null;
    }
  }

  function flushSync() {
    if (!engineReady) return Promise.resolve(null);
    if (activeFlush) return activeFlush;
    if (nativeMutationDepth > 0) return Promise.resolve(lastCart);

    activeFlush = runFlushLoop().finally(() => {
      activeFlush = null;
      if (completedRevision < requestedRevision && nativeMutationDepth === 0) {
        window.setTimeout(() => flushSync(), 0);
      }
    });
    return activeFlush;
  }

  function requestSync(delay = DEFAULT_DEBOUNCE, cartHint = null) {
    if (!engineReady) return Promise.resolve(null);
    cartKnownClean = false;
    requestedRevision += 1;
    if (cartHint?.items) latestCartHint = cartHint;
    window.clearTimeout(debounceTimer);
    setSyncing(true);

    if (nativeMutationDepth > 0) return Promise.resolve(lastCart);
    if (delay <= 0) return flushSync();

    debounceTimer = window.setTimeout(() => flushSync(), delay);
    return Promise.resolve(lastCart);
  }

  async function forceSync() {
    if (!engineReady) return null;
    requestedRevision += 1;
    window.clearTimeout(debounceTimer);
    await waitForNativeIdle();
    await flushSync();
    if (completedRevision < requestedRevision) await flushSync();
    return lastCart;
  }

  function bindCartEvents() {
    document.addEventListener('nawah:cart-mutation-start', () => {
      nativeMutationDepth += 1;
      cartKnownClean = false;
      window.clearTimeout(debounceTimer);
      setSyncing(true);
    });

    document.addEventListener('nawah:cart-mutation-end', (event) => {
      nativeMutationDepth = Math.max(0, nativeMutationDepth - 1);
      const cart = event.detail?.success ? event.detail?.cart : null;
      requestSync(0, cart);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source === SOURCE || event?.source === 'cart-items') return;
          requestSync(60, event?.cartData || null);
        });
      } catch (error) {
        console.warn('NAWAH gift cart subscription unavailable:', error);
      }
    }

    document.addEventListener('nawah:cart-updated', (event) => requestSync(60, event.detail?.cart || null));
    window.addEventListener('pageshow', () => requestSync(0));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') requestSync(80);
    });
  }

  function checkoutButtonFromEvent(event) {
    const target = event.target instanceof Element ? event.target.closest('[name="checkout"], #checkout, #CartDrawer-Checkout') : null;
    return target instanceof HTMLButtonElement || target instanceof HTMLInputElement ? target : null;
  }

  function setCheckoutBusy(button, busy) {
    if (!button) return;
    button.disabled = Boolean(busy);
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }

  function canCheckoutImmediately() {
    return cartKnownClean && !syncing && !activeFlush && nativeMutationDepth === 0 && completedRevision >= requestedRevision;
  }

  async function beginCheckout(button) {
    if (checkoutInProgress) return;
    checkoutInProgress = true;
    setCheckoutBusy(button, true);
    clearCartErrors();

    try {
      const cart = await forceSync();
      if (!cart || planHasChanges(buildPlan(cart, await calculateDesired(cart)))) {
        throw new Error(t('gift_quantities_still_updating', 'Gift quantities are still updating.'));
      }
      window.location.assign(CHECKOUT_URL);
    } catch (error) {
      showCheckoutError(t('gifts_wait_before_checkout', 'Complimentary gifts are still updating. Please wait a moment and try checkout again.'));
      console.error('NAWAH checkout gift validation failed:', error);
      checkoutInProgress = false;
      setCheckoutBusy(button, false);
    }
  }

  function bindCheckoutValidation() {
    document.addEventListener(
      'click',
      (event) => {
        const button = checkoutButtonFromEvent(event);
        if (!button || canCheckoutImmediately()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        beginCheckout(button);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;
        const isCheckout = submitter?.name === 'checkout' || submitter?.id === 'checkout' || submitter?.id === 'CartDrawer-Checkout';
        if (!isCheckout || canCheckoutImmediately()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        beginCheckout(submitter);
      },
      true
    );
  }

  async function hydrateGiftPresentation(node) {
    const id = numericVariantId(node.dataset.nawahGiftVariant);
    if (!Number.isFinite(id)) return;
    const meta = await giftVariantMeta(id);
    if (!meta) return;
    const title = node.querySelector('[data-nawah-gift-title]');
    const imageHost = node.querySelector('[data-nawah-gift-image]');
    if (title) title.textContent = meta.variantTitle && meta.variantTitle !== 'Default Title' ? `${meta.productTitle} — ${meta.variantTitle}` : meta.productTitle;
    if (imageHost && meta.image) imageHost.innerHTML = `<img src="${String(meta.image).replace(/"/g, '&quot;')}" alt="" loading="lazy">`;
  }

  async function hydrateOfferPresentations() {
    const rows = [...document.querySelectorAll('[data-nawah-gift-variant]')];
    await Promise.all(rows.map((node) => hydrateGiftPresentation(node)));
    document.querySelectorAll('[data-nawah-inline-offer]').forEach((node) => {
      const names = [...node.querySelectorAll('[data-nawah-gift-title]')].map((el) => el.textContent.trim()).filter(Boolean);
      const target = node.querySelector('[data-nawah-inline-gift-list]');
      if (target && names.length) target.textContent = names.join(' + ');
    });
  }

  function init() {
    document.documentElement.dataset.nawahGiftEngineVersion = VERSION;

    if (!engineReady) {
      document.documentElement.dataset.nawahGiftEngine = 'not-ready';
      console.info('NAWAH gift engine is disabled until at least one approved gift configuration is available to the Online Store.');
      return;
    }

    document.documentElement.dataset.nawahGiftEngine = 'ready';
    bindCartEvents();
    bindCheckoutValidation();
    hydrateOfferPresentations().catch((error) => console.warn('NAWAH gift presentation hydration failed:', error));
    requestSync(0);
  }

  window.NawahGiftEngine = {
    version: VERSION,
    get ready() { return engineReady; },
    get clean() { return canCheckoutImmediately(); },
    sync: forceSync,
    schedule: requestSync,
    calculateDesired,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
